import { useIntl, FormattedMessage } from "react-intl";

import { PhotoIdVerificationStatus } from "graphql_globals";
import { useA11y } from "common/accessibility";
import Button from "common/core/button";
import LoadingIndicator from "common/core/loading_indicator";
import type { ChildRenderProps } from "common/transactions/details";
import { DetailsDescriptionList } from "common/transactions/details/common";
import { DescriptionListItem } from "common/core/description_list";
import { Heading } from "common/core/typography";
import { useCopy } from "util/clipboard";
import { useDocumentTitles } from "util/document_title";
import { useQuery } from "util/graphql";
import IdentityTimeline from "common/identity/timeline";
import {
  CredentialAnalysisStatusBadge,
  BiometricStatusBadge,
} from "common/details/identity/signer/attributes";
import { getProofRequirementTexts } from "common/details/identity/signer/profile";

import LimitedDetailsQuery, {
  type LimitedDetails_documentBundle_DocumentBundle as DocumentBundle,
} from "./index.query.graphql";
import Styles from "./index.module.scss";

function ProofRequirement({
  signer,
}: {
  signer: DocumentBundle["organizationTransaction"]["customerSigners"][0];
}) {
  const proofRequirementTexts = getProofRequirementTexts(signer.proofRequirement);
  const definition = proofRequirementTexts.map((text, index) => <div key={index}>{text}</div>);

  return proofRequirementTexts.length > 0 ? (
    <DescriptionListItem
      term={
        <FormattedMessage
          id="e7486c13-fad3-407e-9356-ea1cbeb928c2"
          defaultMessage="Verification method"
        />
      }
      definition={definition}
    />
  ) : null;
}

function LimitedAttempts({
  signer,
}: {
  signer: DocumentBundle["organizationTransaction"]["customerSigners"][0];
}) {
  const attempts = signer.signerIdentities ?? [];

  return (
    <>
      {attempts.map((attempt, index) => {
        return (
          <section className={Styles.section} key={index}>
            <Heading className={Styles.attemptHeading} textStyle="headingFive" level="h3">
              <FormattedMessage
                id="a0251bcf-b735-4b67-a0e2-29747a01a08a"
                defaultMessage="Verification Attempt {number}"
                values={{ number: attempts.length - index }}
              />
            </Heading>
            <DetailsDescriptionList key={index}>
              <DescriptionListItem
                term={
                  <FormattedMessage
                    id="7e3dcaf4-3ac0-4c80-bc8d-accbbe1e2faa"
                    defaultMessage="Credential analysis"
                  />
                }
                definition={
                  <CredentialAnalysisStatusBadge
                    status={attempt.photoId?.statusV2 ?? PhotoIdVerificationStatus.UNKNOWN}
                    failedVerificationTypes={attempt.photoId?.failedVerificationTypes ?? []}
                  />
                }
              />

              <DescriptionListItem
                term={
                  <FormattedMessage
                    id="f62aa2eb-1c3c-4cb6-b351-cc75f00c2032"
                    defaultMessage="Biometric comparison"
                  />
                }
                definition={
                  <BiometricStatusBadge
                    status={attempt.photoId?.statusV2 ?? PhotoIdVerificationStatus.UNKNOWN}
                    failedVerificationTypes={attempt.photoId?.failedVerificationTypes ?? []}
                    proofRequirement={signer.proofRequirement}
                    hasSelfie={Boolean(attempt.photoId?.selfiePicture)}
                  />
                }
              />
            </DetailsDescriptionList>
            <IdentityTimeline
              identityTimeline={attempt.identityTimeline}
              options={{ horizontal: true }}
            />
          </section>
        );
      })}
    </>
  );
}

function LimitedSigner({
  signer,
  signerNumber,
}: {
  signer: DocumentBundle["organizationTransaction"]["customerSigners"][0];
  signerNumber: number;
}) {
  const { copy, recentlyCopied } = useCopy();

  const transactionAccessLink = signer.transactionAccessLink;
  const userId = signer.userId;
  const emailAddress = signer.email;

  const accessLinkButton = (
    <Button
      variant="tertiary"
      buttonSize="condensed"
      buttonColor="action"
      withIcon={{
        name: recentlyCopied ? "check" : "copy",
        placement: "left",
      }}
      onClick={() => copy(transactionAccessLink ?? "")}
    >
      {recentlyCopied ? (
        <FormattedMessage id="11100a77-8149-439d-b6bf-1c219901a668" defaultMessage="Copied!" />
      ) : (
        <FormattedMessage id="04f8512b-dbb8-42cc-a167-1216ea0e574a" defaultMessage="Copy link" />
      )}
    </Button>
  );

  return (
    <div className={Styles.limitedLayout}>
      <Heading textStyle="headingFour" level="h2">
        <FormattedMessage
          id="6edc18fc-1a51-47ca-b2eb-b687ac39412f"
          defaultMessage="Recipient {number}"
          values={{ number: signerNumber }}
        />
      </Heading>
      <section className={Styles.section}>
        <DetailsDescriptionList>
          <DescriptionListItem
            term={
              <FormattedMessage
                id="875b0a42-35d4-4c2d-8e95-12aa9f7a3e6e"
                defaultMessage="User ID"
              />
            }
            definition={userId}
          />

          <DescriptionListItem
            term={
              <FormattedMessage
                id="4ce699af-3c26-4918-963d-b46a2dbdde47"
                defaultMessage="Email address"
              />
            }
            definition={emailAddress}
          />

          <ProofRequirement signer={signer} />

          {transactionAccessLink && transactionAccessLink.length > 0 && (
            <DescriptionListItem
              term={
                <FormattedMessage
                  id="cd3732ac-e19b-427a-88d4-9e32cebd3ebe"
                  defaultMessage="Access link"
                />
              }
              definition={accessLinkButton}
            />
          )}
        </DetailsDescriptionList>
      </section>
      <LimitedAttempts key={signer.id} signer={signer} />
    </div>
  );
}

function LimitedDetails(props: { bundle: DocumentBundle }) {
  const { bundle } = props;
  const customerSigners = bundle.organizationTransaction.customerSigners;

  return (
    <>
      {customerSigners.map((signer, index) => (
        <LimitedSigner key={signer.id} signer={signer} signerNumber={index + 1} />
      ))}
    </>
  );
}

type Props = ChildRenderProps;

function LimitedDetailsWrapper(props: Props) {
  const { data, loading } = useQuery(LimitedDetailsQuery, {
    variables: { documentBundleId: props.bundle.id },
  });
  const intl = useIntl();
  useA11y().useDocumentEntitler({
    title: intl.formatMessage(useDocumentTitles().transactionDetailsLimitedDetails),
  });

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  if (data.documentBundle?.__typename !== "DocumentBundle") {
    throw new Error(`Expected DocumentBundle, got ${data.documentBundle?.__typename}`);
  }

  return <LimitedDetails bundle={data.documentBundle} />;
}

export default LimitedDetailsWrapper;
